import { Component, ElementRef, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import Chart from 'chart.js/auto';
import { PieChart } from '../survey-reporting-dashboard.component';

@Component({
  selector: 'app-survey-reporting-chart',
  templateUrl: './survey-reporting-chart.component.html',
  styleUrls: ['./survey-reporting-chart.component.scss'],
  standalone: false,
})
export class SurveyReportingChartComponent implements OnInit, OnChanges {
  @Input() completionPercentage: number;
  @Input() pieChart: PieChart;
  @ViewChild('chartCanvas', { static: true }) chartCanvas: ElementRef<HTMLCanvasElement>;

  private chartInstance: Chart;

  constructor() {}

  ngOnInit(): void {
    this.renderChart();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['pieChart'] && !changes['pieChart'].firstChange) {
      this.chartInstance.data.labels = changes['pieChart'].currentValue.labels;
      this.chartInstance.data.datasets[0].data = changes['pieChart'].currentValue.data;
      this.chartInstance.data.datasets[0].backgroundColor = changes['pieChart'].currentValue.colors[0].backgroundColor;
      this.chartInstance.update();
    }
  }

  private renderChart(): void {
    if (!this.chartCanvas) return;

    // Destroy previous chart if exists
    if (this.chartInstance) {
      this.chartInstance.destroy();
    }

    // Prepare Chart.js config from PieChart input
    const data = {
      labels: this.pieChart.labels,
      datasets: [
        {
          data: this.pieChart.data,
          backgroundColor: this.pieChart.colors[0].backgroundColor,
        },
      ],
    };

    // Convert legacy options to Chart.js v4 options
    const options: any = {
      responsive: true,
      maintainAspectRatio: true,
      aspectRatio: 2, // Control chart size
      plugins: {
        legend: {
          display: !!this.pieChart.legend,
          position: 'bottom',
          labels: {
            font: {
              size: 12, // Consistent font size
            },
          },
        },
        title: this.pieChart.options?.title
          ? {
              display: this.pieChart.options.title.display,
              text: this.pieChart.options.title.text,
              font: {
                size: this.pieChart.options.title.fontSize,
                weight: this.pieChart.options.title.fontStyle || 'normal',
              },
              color: this.pieChart.options.title.fontColor,
            }
          : undefined,
        tooltip: {
          enabled: this.pieChart.options?.tooltips?.enabled !== false,
          callbacks: {
            // Format tooltips to show percentages
            label: function (context) {
              const dataset = context.dataset;
              const total = dataset.data.reduce((acc, data) => acc + data, 0);
              const currentValue = dataset.data[context.dataIndex];
              const percentage = Math.round((currentValue / total) * 100);
              return `${context.label}: ${percentage}%`;
            },
          },
        },
        // This replaces the old chartjs-plugin-labels
        datalabels: {
          display: true,
          formatter: (value, ctx) => {
            const dataset = ctx.chart.data.datasets[0];
            const total = dataset.data.reduce((acc, data) => acc + data, 0);
            const percentage = Math.round((value / total) * 100);
            return percentage > 0 ? percentage + '%' : '';
          },
          // Center the labels
          align: 'center',
          anchor: 'center',
        },
      },
    };

    // Create the chart with proper type
    this.chartInstance = new Chart(this.chartCanvas.nativeElement.getContext('2d'), {
      type: 'pie',
      data,
      options,
    });
  }
}
